import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';


/**
 * Component for wrapping text elements in the legends.
 *
 * Any properties in addition to those defined on this component will be passed through
 * and applied to the inner svg <text> element
 *
 * @property height - height of this text group in pixels for wrapping calculations
 * @property width - width of this text group in pixels for wrapping calculations
 * @property fontSize - fontSize for this text group in pixels for wrapping calculations
 * @property children - string to be wrapped based on rough measurements
 *
 * @example
 * return (
 *   <WrappingText
 *     fontSize={12}
 *     width={this.props.width - 250}
 *     height={400}
 *     fontFamily="arial,sans-serif"
 *     fontSize={12}>
 *     {'Some text to wrap If too long'}
 *   </WrappingText>
 * )
 */
export default class WrappingText extends PureComponent {

  static propTypes = {
    children: PropTypes.node,
    fontSize: PropTypes.number,
    height: PropTypes.number,
    width: PropTypes.number
  };

  static defaultProps = {
    fontSize: 12,
    height: 100,
    width: 100
  }

  calculateRows = () => {
    const {children, fontSize, width} = this.props

    // Approximation of arial character pixel width
    const charSize = fontSize * 0.5

    // Adds a tspan for line to generated elements
    const addElement = (elements, line) => {
      const count = _.size(elements)
      elements.push(
        <tspan
          key={count}
          x={0}
          dy={charSize * 2.8}
          fontSize={fontSize}
          children={line}
        />
      )
    }

    // Reduce the children array into array of measured tspan lines
    return _.reduce(children, (elements, child) => {
      const lastLine = _.chain(String(child))
        .split(' ')
        .reduce((line, word) => {
          const lineWidth = _.size(line) * charSize
          const wordWidth = _.size(word) * charSize

          if (lineWidth + wordWidth > width) {
            addElement(elements, line)
            line = ''
          }

          return line + word + ' '
        }, '')
        .value()

      addElement(elements, lastLine)
      addElement(elements, <tspan>&nbsp;</tspan>)

      return elements
    }, [])
  }

  render() {
    const {fontSize, width, height, ...rest} = this.props;

    return (
      <text {...rest} textAnchor="top" children={this.calculateRows()}/>
    )
  }
}

